/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/*
 * Copyright (C) America Móvil S.A.B de C.V - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Juan Manuel Garcia Junco Moreno <manue.garcia@ironbit.com.mx>, February 2020
 */
import { Nullable } from '../types';
import { ICollection, Iterator, IEnumerable } from './types';
import { LinkedListNode } from './LinkedListNode';

/**
 * A TypeScript implementation of a LinkedList.
 */
export class LinkedList<T> implements IEnumerable<T>, ICollection<T> {
    private size = 0;
    private head: Nullable<LinkedListNode<T>>;
    private tail: Nullable<LinkedListNode<T>>;

    /**
     * The number of nodes in the list.
     */
    public get length(): number {
        return this.size;
    }

    /**
     * Retrieve the value of of the given node.
     *
     * @returns The value of the head.
     */
    public get(index: number): Nullable<T> {
        let i = 0;
        let node = this.head;

        while (node && i <= index) {
            if (index === i) return node.value;
            i += 1;
            node = node.next;
        }

        throw new Error(`Index \`${index}\` is out of bound`);
    }

    /**
     * Remove the element at the end of the list.
     *
     * @returns The value of the removed element.
     */
    public pop(): Nullable<T> {
        if (!this.head) {
            return null;
        }

        const { value } = this.tail!;

        if (this.head === this.tail) {
            // There is only one node in linked list.
            this.head = null;
            this.tail = null;
            this.size -= 1;

            return value;
        }

        // If there are many nodes in linked list...
        // Rewind to the last node and delete "next" link for the node before the last one.
        let currentNode = this.head;
        while (currentNode.next) {
            if (!currentNode.next.next) {
                this.size -= 1;
                currentNode.next = null;
            } else {
                currentNode = currentNode.next;
            }
        }

        this.tail = currentNode;

        return value;
    }

    /**
     * Insert an element at the end of the list.
     *
     * @param value Value to insert.
     * @returns The new length of the list.
     */
    public push(value: T): number {
        const node = new LinkedListNode(value, null);

        // If there is no head yet let's make new node a head.
        if (!this.head) {
            this.head = node;
            this.tail = node;
            this.size += 1;

            return this.size;
        }

        // Attach new node to the end of linked list.
        // Since prepend and append both set the tail of the list we're sure this is defined.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion, @typescript-eslint/no-non-null-assertion
        this.tail!.next = node;
        this.tail = node;
        this.size += 1;

        return this.size;
    }

    /**
     * Removes the element at the beginning of the list.
     *
     * @returns The removed node.
     */
    public shift(): Nullable<T> {
        if (!this.head) {
            return null;
        }

        this.size -= 1;

        const { head } = this;

        if (this.head.next) {
            this.head = this.head.next;
        } else {
            this.head = null;
            this.tail = null;
        }

        return head.value;
    }

    /**
     * Insert an element at the beginning of the list.
     *
     * @param value Value to insert.
     * @returns The linked list instance.
     */
    public unshift(value: T): number {
        const node = new LinkedListNode<T>(value, this.head);

        if (this.head) {
            node.next = this.head;
            this.head = node;
        } else {
            this.head = node;
            this.tail = node;
        }

        this.size += 1;

        return this.size;
    }

    /**
     * Remove a node with the given value.
     *
     * @param value Node value.
     */
    public remove(value: T): void {
        if (!this.head) {
            throw new Error('Cannot remove elements on an empty list');
        }

        // If the head must be deleted then make next node that is differ
        // from the head to be a new head.
        if (this.head.value === value) {
            this.size -= 1;
            this.head = this.head.next;
        }

        let currentNode = this.head;

        if (currentNode !== null) {
            // If next node must be deleted then make next node to be a next next one.
            while (currentNode.next) {
                if (currentNode.next.value === value) {
                    this.size -= 1;
                    currentNode.next = currentNode.next.next;
                } else {
                    currentNode = currentNode.next;
                }
            }
        }

        // Check if tail must be deleted.
        if (this.tail!.value === value) {
            this.tail = currentNode;
        }
    }

    /**
     * Creates a new LinkedList by mapping the value of each node with a function.
     *
     * @param iterator Function used to map values.
     * @returns A new LinkedList with the mapped values.
     */
    public map<U>(iterator: Iterator<T, U>): LinkedList<U> {
        const linkedList = new LinkedList<U>();

        this.forEach(value => {
            linkedList.push(iterator(value));
        });

        return linkedList;
    }

    /**
     * Traverse each node in the list.
     *
     * @param iterator A function to call for each node in the list.
     */
    public forEach(iterator: Iterator<T, unknown>): void {
        let node = this.head;

        while (node) {
            iterator(node.value);
            node = node.next;
        }
    }
}



// WEBPACK FOOTER //
// ./src/packages/core/lib/collections/LinkedList.ts